import { RTKQueryTag } from 'src/common/constants';
import { apiSlice } from 'src/features/api/api-slice';

import {
  ExtendedSearchKind,
  type ExtendedSearchProps,
  type GetHacktivitiesSearchQueryParams,
  type GetSearchProgressSearchQueryParams,
  type HacktivitiesApiResponse,
  type SearchProgressResponse,
} from './content.types';

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getHacktivitiesSearch: builder.query<HacktivitiesApiResponse, GetHacktivitiesSearchQueryParams>({
      query: ({
        kind,
        difficulty,
        order,
        roomType,
        contentSubType,
        povTagFilter,
        page,
        searchText,
        userProgress,
        newLimit,
        from,
      }: GetHacktivitiesSearchQueryParams) => ({
        url: 'hacktivities/extended-search',
        params: {
          kind,
          difficulty,
          order,
          roomType,
          contentSubType,
          povTagFilter,
          page,
          searchText,
          userProgress,
          limit: newLimit ?? 12,
          from,
        },
      }),
      providesTags: () => [{ type: RTKQueryTag.Hacktivities }],
      transformResponse: (response: HacktivitiesApiResponse) => {
        const uniqueDocsMap = new Map<string, ExtendedSearchProps>();

        for (const item of response.data.docs) {
          let key: string;
          if (item.kind === ExtendedSearchKind.MODULES) {
            key = item.moduleURL;
          } else if (item.kind === ExtendedSearchKind.SOC_SIM) {
            key = item.scenarioId || item.code;
          } else if (item.kind === ExtendedSearchKind.THREAT_HUNTING) {
            key = item.scenarioId || item.code;
          } else {
            key = item.code;
          }

          if (!uniqueDocsMap.has(key)) {
            uniqueDocsMap.set(key, item);
          }
        }

        const uniqueDocs: ExtendedSearchProps[] = [...uniqueDocsMap.values()];

        return {
          ...response,
          data: {
            ...response.data,
            docs: uniqueDocs,
            // Preserve the original totalDocs count from the API response
            totalDocs: response.data.totalDocs,
          },
        };
      },
    }),
    getSearchProgress: builder.query<SearchProgressResponse, GetSearchProgressSearchQueryParams>({
      query: ({ roomCodes, networkCodes, pathIds, moduleIds }) => ({
        url: 'hacktivities/search-progress',
        params: {
          roomCodes,
          networkCodes,
          pathIds,
          moduleIds,
        },
      }),
    }),
  }),
});

export const { useGetHacktivitiesSearchQuery, useGetSearchProgressQuery, useLazyGetSearchProgressQuery } =
  extendedApiSlice;
